import { useEffect, useRef, useCallback, useState } from 'react';
import { marked, parseInline, type Tokens } from 'marked';
import hljs from 'highlight.js';
import DOMPurify from 'dompurify';
import { hasMediaReferences, resolveMediaReferences } from '@/lib/mediaResolver';
import { parseFrontMatter } from '@/lib/frontMatter';
import { createHeadingSlugger } from '@/lib/headingSlug';
import 'highlight.js/styles/github-dark.css';

// Mermaid's SVG carries diagram-source text through to markup, so it is
// attacker-controlled repo content and must be sanitized before innerHTML —
// mermaid's own securityLevel:'strict' is the renderer's promise, not our
// control, and a mermaid bug or upgrade would silently become stored XSS here.
//
// Kept byte-identical to apps/website/src/components/MermaidDiagram.tsx's
// SANITIZE_CONFIG, and for the same reason: DOMPurify's defaults drop
// <foreignObject>, which is where mermaid puts every flowchart node label, so
// sanitizing without it renders diagrams as empty boxes. script, on* handlers,
// javascript: URLs and iframes are still stripped from inside it. Change both
// copies together, and do not widen either.
const MERMAID_SANITIZE_CONFIG = {
  ADD_TAGS: ['foreignObject'],
  HTML_INTEGRATION_POINTS: { foreignobject: true, 'annotation-xml': true },
};

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** Base path for resolving relative .md links (e.g. "owner/repo") */
  basePath?: string;
  onNavigate?: (path: string) => void;
  /** Called after innerHTML is written to the DOM — use to delay anything
   *  that queries the rendered content (e.g. TTS hover-button setup). */
  onRendered?: () => void;
}

/**
 * Track the `.dark` class shared-react-ui's applyTheme toggles on <html>.
 *
 * Mermaid bakes its palette into the SVG at render time, so — unlike the CSS
 * around it — a diagram cannot follow a theme change on its own. This drives a
 * re-render when the user switches theme, and gives the initial render the
 * right palette to start from.
 */
function useIsDarkTheme(): boolean {
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync(); // The class may have changed between first render and this effect.
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

/** GFM alert types: > [!NOTE], > [!TIP], etc. */
const ALERT_TYPES: Record<string, { icon: string; color: string; label: string }> = {
  NOTE: { icon: 'ℹ️', color: 'blue', label: 'Note' },
  TIP: { icon: '💡', color: 'green', label: 'Tip' },
  IMPORTANT: { icon: '❗', color: 'purple', label: 'Important' },
  WARNING: { icon: '⚠️', color: 'yellow', label: 'Warning' },
  CAUTION: { icon: '🔴', color: 'red', label: 'Caution' },
};

const ALERT_CSS_CLASS: Record<string, string> = {
  NOTE: 'alert-note',
  TIP: 'alert-tip',
  IMPORTANT: 'alert-important',
  WARNING: 'alert-warning',
  CAUTION: 'alert-caution',
};

/**
 * When content contains raw HTML block elements (e.g. <p>**bold**</p>),
 * marked treats them as opaque HTML and skips inline markdown processing
 * inside them. This pre-processor runs parseInline() on the text inside
 * common block elements so inline markdown renders correctly even when
 * the surrounding structure is HTML rather than markdown.
 */
function processHtmlInlineMarkdown(content: string): string {
  if (!/<(?:p|li|td|th|dd|dt)\b/i.test(content)) return content;
  return content.replace(
    /(<(?:p|li|td|th|dd|dt)[^>]*>)([\s\S]*?)(<\/(?:p|li|td|th|dd|dt)>)/gi,
    (full, open, inner, close) => {
      if (!/[*_`[~]/.test(inner)) return full;
      return open + (parseInline(inner) as string) + close;
    },
  );
}

function configureMarked() {
  const renderer = new marked.Renderer();

  // Syntax-highlighted code blocks with copy button
  renderer.code = ({ text, lang }: { text: string; lang?: string }) => {
    if (lang === 'mermaid') {
      return `<div class="mermaid-block" data-mermaid="${encodeURIComponent(text)}"><pre class="bg-slate-100 dark:bg-slate-900 p-4 rounded text-sm text-slate-600 dark:text-slate-300">Loading diagram...</pre></div>`;
    }

    let highlighted: string;
    if (lang && hljs.getLanguage(lang)) {
      highlighted = hljs.highlight(text, { language: lang }).value;
    } else {
      highlighted = hljs.highlightAuto(text).value;
    }

    return `<div class="code-block-wrapper">
      <button class="copy-btn" data-code="${encodeURIComponent(text)}">Copy</button>
      ${lang ? `<span class="code-lang">${lang}</span>` : ''}
      <pre class="hljs"><code>${highlighted}</code></pre>
    </div>`;
  };

  // Task list checkboxes.
  //
  // `item.text` is the item's RAW markdown source — emitting it directly leaves
  // inline syntax unrendered (`**bold**`, `` `code` ``, links) in every list
  // item. The body must come from parsing `item.tokens`, which is also where
  // marked puts the task checkbox, so this must NOT prepend a checkbox of its
  // own (that renders two). Styling the checkbox is `renderer.checkbox`'s job.
  //
  // Regular `function` — not an arrow — so `this.parser` is bound by marked.
  renderer.checkbox = ({ checked }: { checked: boolean }) =>
    checked
      ? '<input type="checkbox" checked disabled class="mr-2 accent-blue-600 pointer-events-none" />'
      : '<input type="checkbox" disabled class="mr-2 pointer-events-none" />';

  renderer.listitem = function (item: Tokens.ListItem) {
    // Loose-vs-tight <p> wrapping is already baked into item.tokens in marked
    // v18, so parse() takes the tokens alone.
    const body = this.parser.parse(item.tokens);
    return item.task
      ? `<li class="task-list-item list-none">${body}</li>\n`
      : `<li>${body}</li>\n`;
  };

  // marked v18 does not emit id attributes on headings by default. Each
  // heading gets one here, unique within the document (lib/headingSlug):
  // the "On this page" list reads the rendered headings and links to
  // these ids. configureMarked runs once per render, so the slugger starts
  // afresh for every document.
  const slug = createHeadingSlugger();
  renderer.heading = ({ text, depth, raw }: { text: string; depth: number; raw: string }) =>
    `<h${depth} id="${slug(raw)}">${text}</h${depth}>\n`;

  return renderer;
}

function processAlerts(html: string): string {
  // Match blockquotes that start with [!TYPE]
  return html.replace(
    /<blockquote>\s*<p>\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*(?:<br\s*\/?>)?\s*([\s\S]*?)<\/p>\s*<\/blockquote>/gi,
    (_match, type: string, body: string) => {
      const upperType = type.toUpperCase();
      const alert = ALERT_TYPES[upperType];
      if (!alert) return _match;
      const cssClass = ALERT_CSS_CLASS[upperType];
      return `<div class="alert-block ${cssClass}">
        <p class="alert-block-title">${alert.icon} ${alert.label}</p>
        <div class="alert-block-body">${body}</div>
      </div>`;
    }
  );
}

function processRelativeLinks(html: string, basePath?: string, onNavigate?: (path: string) => void): string {
  if (!basePath) return html;
  // Mark relative .md links with data attribute for click handling
  return html.replace(
    /href="(\.\/|\.\.\/)?([^"]*\.md)(#[^"]*)?"/g,
    (_match, _prefix: string, path: string, anchor: string = '') => {
      return `href="#" data-doc-link="${path}${anchor}" class="doc-internal-link"`;
    }
  );
}

export function MarkdownRenderer({ content, className = '', basePath, onNavigate, onRendered }: MarkdownRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Held in a ref so it never re-triggers the rendering effect when the
  // caller passes a new inline arrow function on each render.
  const onRenderedRef = useRef(onRendered);
  onRenderedRef.current = onRendered;

  // Mermaid diagrams are re-rendered when this flips — see useIsDarkTheme.
  const isDark = useIsDarkTheme();

  // Captured images are stored as stable media:// references; resolve them to
  // short-lived authenticated URLs before rendering (photo-capture Phase 4).
  // Content without media refs renders synchronously, unchanged.
  const [resolvedContent, setResolvedContent] = useState<string | null>(null);
  useEffect(() => {
    if (!hasMediaReferences(content)) {
      setResolvedContent(null);
      return;
    }
    let cancelled = false;
    resolveMediaReferences(content).then((r) => {
      if (!cancelled) setResolvedContent(r);
    });
    return () => { cancelled = true; };
  }, [content]);
  const displayContent = resolvedContent ?? content;

  const handleClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;

    // Handle copy buttons
    const copyBtn = target.closest('.copy-btn') as HTMLElement;
    if (copyBtn) {
      e.preventDefault();
      const code = decodeURIComponent(copyBtn.dataset.code || '');
      navigator.clipboard.writeText(code).then(() => {
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = 'Copy'; }, 2000);
      });
      return;
    }

    // Handle internal doc links
    const docLink = target.closest('[data-doc-link]') as HTMLElement;
    if (docLink && onNavigate) {
      e.preventDefault();
      onNavigate(docLink.dataset.docLink || '');
    }
  }, [onNavigate]);

  useEffect(() => {
    if (!containerRef.current) return;

    const renderer = configureMarked();
    // Strip the YAML front-matter header before rendering (sdx#1109) — otherwise
    // marked turns `tags: […]` + the closing `---` into a setext heading.
    const { body } = parseFrontMatter(displayContent);
    const preprocessed = processHtmlInlineMarkdown(body);
    const rawHtml = marked(preprocessed, { gfm: true, breaks: false, renderer }) as string;
    let processed = processAlerts(rawHtml);
    processed = processRelativeLinks(processed, basePath, onNavigate);
    const sanitized = DOMPurify.sanitize(processed, {
      ADD_TAGS: ['input'],
      ADD_ATTR: ['data-code', 'data-mermaid', 'data-doc-link', 'checked', 'disabled', 'type', 'id'],
    });
    containerRef.current.innerHTML = sanitized;
    // A table cannot be narrower than its columns. Each one goes in a box
    // that scrolls sideways on its own (.docs-table-scroll, docs-shared.css),
    // so a wide table does not widen the whole page around it.
    containerRef.current.querySelectorAll('table').forEach((table) => {
      const box = document.createElement('div');
      box.className = 'docs-table-scroll';
      table.replaceWith(box);
      box.appendChild(table);
    });
    onRenderedRef.current?.();

    // Initialize mermaid blocks if mermaid is available
    const mermaidBlocks = containerRef.current.querySelectorAll('.mermaid-block');
    if (mermaidBlocks.length > 0) {
      import('mermaid').then(({ default: mermaid }) => {
        // Match the app's theme. Mermaid renders onto a transparent background,
        // so its own palette is the only thing keeping a diagram legible: the
        // 'dark' theme draws pale strokes and labels, which vanish against a
        // light page. Re-runs on isDark, since the SVG cannot re-theme itself.
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? 'dark' : 'default',
          securityLevel: 'strict',
        });
        mermaidBlocks.forEach(async (block, i) => {
          const source = decodeURIComponent((block as HTMLElement).dataset.mermaid || '');
          try {
            const { svg } = await mermaid.render(`mermaid-${i}-${Date.now()}`, source);
            block.innerHTML = DOMPurify.sanitize(svg, MERMAID_SANITIZE_CONFIG);
          } catch (err) {
            // The mermaid source is attacker-controlled repo content. On the
            // error path it must NEVER reach an HTML sink: build the node with
            // textContent so the raw source (e.g. `<img src=x onerror=...>`) is
            // rendered as inert text, not parsed as HTML. Do not switch this
            // back to innerHTML.
            const pre = document.createElement('pre');
            pre.className = 'text-red-700 dark:text-red-400 text-sm p-4 bg-red-50 dark:bg-slate-900 rounded';
            pre.textContent = `${(err as Error).message}\n\n${source}`;
            block.innerHTML = '';
            block.appendChild(pre);
          }
        });
      }).catch(() => {
        // mermaid not installed — leave placeholder
      });
    }
  }, [displayContent, basePath, onNavigate, isDark]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, [handleClick]);

  return (
    <div
      ref={containerRef}
      className={className}
    />
  );
}
